import { createClient } from "@connectrpc/connect";
import type { MultiPolygon } from "geojson";
import { grpcCatalogosDatabase } from "../../../../comun/database/GrpcCatalogosDatabase";
import { grpcTransportProvider } from "../../../../comun/services/grpc/GrpcTransport";
import { mapConnectError } from "../../../../comun/services/grpc/mapConnectError";
import { sortByField } from "../../../../utils/sortUtils";
import { wkbToMultiPolygon } from "../../../../utils/wkbUtils";
import {
  CatalogosTercerosService,
  MunicipioItem,
} from "../../../../generated/grpc/catalogos_terceros_pb";

/** Municipio con `areaGeografica` ya decodificada de WKB a GeoJSON (mismo shape que usa REST en
 *  `MunicipioResponse`). */
export type StoredMunicipio = Omit<MunicipioItem, "areaGeografica"> & {
  areaGeografica: MultiPolygon;
};

/** Municipios **por `departamentoId`**, no catálogo completo: los ~1122 del país no se piden nunca
 *  juntos -pesan ~75 MB sin simplificar, y esa fue la razón por la que `ListMunicipios` como catálogo
 *  plano se eliminó el 2026-09-15-. Es una consulta puntual parametrizada sin watermark, así que no
 *  extiende `BaseGrpcCatalogService` (ver `CatalogosTercerosGrpcService.cs` y
 *  `catalogos_terceros.proto`).
 *
 *  **Dos niveles de cache**:
 *  1. Map en memoria por `departamentoId`, dura la sesión de la página — evita ida y vuelta a
 *     IndexedDB si el usuario elige el mismo departamento varias veces seguidas.
 *  2. `GrpcCatalogosDatabase` (store `"municipios"`, índice por `departamentoId`) — persiste entre
 *     recargas, así la precarga de `catalogSync` solo pega al server la primera vez. Sin este segundo
 *     nivel esa precarga se repetiría entera en cada carga de página, porque el Map nace vacío. */
export class MunicipioByDepartamentoIdGrpcService {
  private readonly _cache = new Map<string, StoredMunicipio[]>();

  async getListAsync(departamentoId: string): Promise<StoredMunicipio[]> {
    const enMemoria = this._cache.get(departamentoId);
    if (enMemoria) return enMemoria;

    const persistidos = sortByField(
      await grpcCatalogosDatabase.getMunicipiosByDepartamentoIdAsync<StoredMunicipio>(
        departamentoId,
      ),
      "nombre",
    );
    if (persistidos.length > 0) {
      this._cache.set(departamentoId, persistidos);
      return persistidos;
    }

    const client = createClient(CatalogosTercerosService, grpcTransportProvider.get());

    let items: StoredMunicipio[];
    try {
      const response = await client.listMunicipiosByDepartamentoId({ departamentoId });
      items = sortByField(
        response.items.map((item) => ({
          ...item,
          areaGeografica: wkbToMultiPolygon(item.areaGeografica),
        })),
        "nombre",
      );
    } catch (error) {
      throw mapConnectError(error);
    }

    // Un departamento sin ningún municipio (`items` vacío) no queda "recordado" como ya pedido en
    // IndexedDB -no hay fila que guardar-, así que ese caso puntual vuelve a pedirse en la próxima
    // carga de página. El Map en memoria sí lo cubre dentro de la misma sesión. Aceptado: es un caso
    // borde y no vale la pena una marca aparte solo para eso.
    if (items.length > 0) {
      await grpcCatalogosDatabase.putMunicipiosAsync(items);
    }
    this._cache.set(departamentoId, items);
    return items;
  }
}

/** Singleton del servicio gRPC de municipios por departamento. */
export const municipioByDepartamentoIdGrpcService = new MunicipioByDepartamentoIdGrpcService();
